import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnInit, signal } from '@angular/core';
import { AnalyticsService } from 'src/app/services/analytics.service';
import { PnpService } from 'src/app/services/pnp.service';
import { StepsControl } from 'src/app/shared/utils/workflows/workflow-steps-control';
import { PolicyProcedureApiService } from '../../../services/policy-procedure-api.service';
import { IRevokePolicyFlow, IRevokePolicySharedData } from '../../revoke-policy.interface';
import { UISpinnerComponent } from '../../../../../ui/ui-spinner/ui-spinner.component';
import { KeyValuePipe, NgForOf, NgIf } from '@angular/common';
import { AddflowConfirmUsersComponent } from '../../../../../shared/popups/addflow-confirm-users/addflow-confirm-users.component';
import { contains, intersection, uniq } from 'underscore';
import { AddPluralSPipe } from 'src/app/shared/pipes/add-plural-s.pipe';
import { clone } from 'lodash';
import { combineLatest } from 'rxjs';
import { EditOnlyNewVersion, GovernanceForm, originalOrder } from '../../../interfaces/policy-and-procedure.interface';
import { removeEmptyFields } from 'src/app/shared/utils/remove-empty-fields';
import { FormsModule } from '@angular/forms';

@Component({
    templateUrl: 'revoke-policy-review.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [FormsModule, NgForOf, AddflowConfirmUsersComponent, NgIf, UISpinnerComponent, AddPluralSPipe, KeyValuePipe,]
})

export class RevokePolicyReviewComponent implements OnInit {
    revokePolicyFlowSharedData: IRevokePolicySharedData;
    stepsControl: StepsControl<IRevokePolicyFlow>;
    error = '';
    revokingPolicy = false;

    newUsers = signal([]);
    existingUsers = signal([]);

    NEW_VERSION_TO_OPTION_DISPLAY: Map<EditOnlyNewVersion, string> = new Map([
        ['Minor', 'Notify required users'],
        ['Patch', 'Do not notify required users']
    ]);

    newVersion: EditOnlyNewVersion = 'Minor';
    originalOrder = originalOrder;
    constructor(
        private cdRef: ChangeDetectorRef,
        private pnpApiService: PolicyProcedureApiService,
        private pnpService: PnpService,
        private analytics: AnalyticsService
    ) { }

    ngOnInit() {
        console.log('selected policy', this.revokePolicyFlowSharedData.selectedPolicy);
        const existingUsersInJobRoles = this.revokePolicyFlowSharedData.selectedUsers.filter(u => u.activeJobRoleTakens && intersection((this.revokePolicyFlowSharedData.selectedPolicy.ackRelevancy?.jobRoles || []), u.activeJobRoleTakens.map(j => j.jobRole)).length > 0).map(u => u.orgUserID);
        const existingUsersInTeams = this.revokePolicyFlowSharedData.selectedUsers.filter(u => u?.allTeams && intersection((this.revokePolicyFlowSharedData.selectedPolicy.ackRelevancy?.teams || []), u.allTeams).length > 0).map(u => u.orgUserID);
        const existingUsersInOrgUserIDs = this.revokePolicyFlowSharedData.selectedUsers.filter(u => contains((this.revokePolicyFlowSharedData.selectedPolicy.ackRelevancy?.orgUserIDs || []), u.orgUserID)).map(u => u.orgUserID);
        const exitingUsersOrgUserIDs = uniq([...existingUsersInJobRoles, ...existingUsersInOrgUserIDs, ...existingUsersInTeams]);
        this.existingUsers.set(this.revokePolicyFlowSharedData.selectedUsers.filter(u => contains(exitingUsersOrgUserIDs, u.orgUserID)));
        this.newUsers.set(this.revokePolicyFlowSharedData.selectedUsers.filter(u => !contains(exitingUsersOrgUserIDs, u.orgUserID)));
    }

    next() {
        this.stepsControl.updateSteps();
        this.stepsControl.nextStep();
        this.cdRef.detectChanges();
    }

    onBack() {
        this.stepsControl.previousStep();
    }

    revokePolicy() {
        // const orgUserIDs = this.revokePolicyFlowSharedData.selectedUsers.map(u => u.orgUserID);
        this.revokingPolicy = true;
        this.cdRef.detectChanges();
        const updateAPIs$ = [];

        if (this.newUsers().length > 0) {
            const updatedOrgUserIDs = uniq([...(this.revokePolicyFlowSharedData.selectedPolicy.ackRelevancy?.orgUserIDs || []), ...this.newUsers().map(u => u.orgUserID)]);
            const newPolicy: GovernanceForm = {
                ...clone(this.revokePolicyFlowSharedData.selectedPolicy),
                governanceVersion: {
                    ...clone(this.revokePolicyFlowSharedData.selectedPolicy.governanceVersion),
                    relevancy: this.revokePolicyFlowSharedData.selectedPolicy.relevancy || undefined,
                    ackRelevancy: this.revokePolicyFlowSharedData.selectedPolicy.ackRelevancy || undefined,
                    governanceDocs: this.revokePolicyFlowSharedData.selectedPolicy.governanceVersion.governanceDocs?.map(doc => doc.docID) || undefined,
                    linkedGovernances: this.revokePolicyFlowSharedData.selectedPolicy.governanceVersion.linkedGovernances?.map(gov => gov.governanceID) || undefined
                }
            };
            if (newPolicy.governanceVersion.ackRelevancy === undefined) {
                newPolicy.governanceVersion.ackRelevancy = {};
            }
            
            newPolicy.governanceVersion.ackRelevancy['orgUserIDs'] = updatedOrgUserIDs;
            newPolicy.governanceVersion.newVersion = this.newVersion;
            newPolicy.governanceVersion.notification = this.NEW_VERSION_TO_OPTION_DISPLAY.get(this.newVersion);
            removeEmptyFields(newPolicy);
            // console.log(newPolicy)
            updateAPIs$.push(this.pnpApiService.updatePolicy(this.revokePolicyFlowSharedData.selectedPolicy.governanceID, this.revokePolicyFlowSharedData.selectedPolicy.governanceVersion.versionID, newPolicy));
        }

        if (this.existingUsers().length > 0) {
            updateAPIs$.push(this.pnpApiService.revokePolicy(this.revokePolicyFlowSharedData.selectedPolicy.governanceID, { orgUserIDs: this.existingUsers().map(u => u.orgUserID) }));
        }
        this.revokePolicyFlowSharedData.newVersion = this.newVersion;
        combineLatest(updateAPIs$).subscribe(([updatedPolicy, ack]) => {
            console.log('updated', updatedPolicy);
            this.revokingPolicy = false;
            this.pnpService.announceAckUpdate();
            this.revokePolicyFlowSharedData.revokeResult = ack;
            if (this.newVersion === 'Minor') {
                this.pnpApiService.sendReminderWhenSelectReAck([this.revokePolicyFlowSharedData.selectedPolicy.governanceID]).subscribe();
            }
            this.cdRef.detectChanges();
            this.next();
            this.analytics.track('P-policy-reassigned', {
                governanceID: this.revokePolicyFlowSharedData.selectedPolicy.governanceID
            });
        });
        // this.next();
    }
}